import { useState, useCallback, useMemo, useEffect } from 'react';
import moment, { Moment } from 'moment';
import { useIntl } from 'react-intl';
import map from 'lodash/map';
import range from 'lodash/range';
import padStart from 'lodash/padStart';

import Grid from '@mui/material/Grid2';
import Radio from '@mui/material/Radio';
import Typography from '@mui/material/Typography';
import { IconButton, Tooltip, type SelectChangeEvent } from '@mui/material';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';

import { isSuperUser } from 'src/utils/authentication';
import { monthFormat, monthYearLongFormat, toString } from 'src/utils/datetime';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import PageLayout from 'src/components/PageLayout';
import Dropdown from 'src/components/Dropdown';
import DataTable from 'src/components/DataTable';
import ProgressBar from 'src/components/ProgressBar';

import {
  useGetPayrolls,
  useCreatePayroll,
  useDeletePayroll,
  useDownloadPayroll,
  useGetPayrollProgress,
} from 'src/pages/PayrollPage/api';

import messages from './messages';

const now = moment();

const year =
  now.month() === 0 && now.date() <= 15 ? now.year() - 1 : now.year();

interface PayrolMonthType {
  date: Moment;
  dateAsString: string;
}

interface PayrollDataState {
  id: string;
  data: Array<UCM.PayrollDataType>;
  meta: UCM.PayrollMetaType;
}

const yearOptions = map(range(year, year - 5), (eachYear) => ({
  label: eachYear.toString(),
  value: eachYear.toString(),
}));

export default function PayrollPage() {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const [selectedYear, setSelectedYear] = useState(year.toString());
  const [selectedMonth, setSelectedMonth] = useState<PayrolMonthType | null>(
    null,
  );

  const [selectedPayroll, setSelectedPayroll] = useState<
    UCM.PayrollDataType | undefined
  >();

  const [checkedMonthPayroll, setCheckedMonthPayroll] =
    useState<UCM.PayrollMetaType | null>(null);

  const [dates, setDates] = useState<Array<string>>([]);
  const [progressBarEnabled, setProgressBarEnabled] = useState(true);

  const { data: payrollData, isPending: isLoading } = useGetPayrolls({
    enabled: !!dates.length,
    dates,
  });

  const { mutate: createPayroll, isPending: isCreating } = useCreatePayroll();
  const { mutate: deletePayroll, isPending: isDeleting } = useDeletePayroll();

  const { data: progressData } = useGetPayrollProgress({
    enabled: isCreating || progressBarEnabled,
  });

  const { isSuccess: isSuccessDownload, isFetching: isDownloading } =
    useDownloadPayroll({
      payroll: selectedPayroll,
      enabled: !!selectedPayroll,
    });

  useEffect(() => {
    setProgressBarEnabled(progressData !== null);
  }, [progressData !== null]);

  useEffect(() => {
    if (isSuccessDownload) {
      setSelectedPayroll(undefined);
    }
  }, [isSuccessDownload]);

  useEffect(() => {
    const localDates = range(1, 13).map((num) => {
      const month = padStart(num.toString(), 2, '0');
      return `${selectedYear}-${month}-01`;
    });
    setDates(localDates);
  }, [selectedYear]);

  const handleChange = useCallback((event: SelectChangeEvent<unknown>) => {
    const value = event.target.value as string;
    setSelectedYear(value);
    setSelectedMonth(null);
  }, []);

  const handleSelectMonth = useCallback((row: PayrollDataState | null) => {
    setSelectedMonth(
      row
        ? {
            date: row.meta.date,
            dateAsString: row.meta.dateAsString,
          }
        : null,
    );
  }, []);

  const pressMonthPayroll = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, meta: UCM.PayrollMetaType) => {
      event.stopPropagation();
      setCheckedMonthPayroll((prevCheckedMonth) =>
        meta.dateAsString === prevCheckedMonth?.dateAsString ? null : meta,
      );
    },
    [],
  );

  const handleGenerate = useCallback(() => {
    if (!checkedMonthPayroll) return;
    createPayroll({ date: checkedMonthPayroll.dateAsString });
  }, [checkedMonthPayroll]);

  const handleDelete = useCallback((payroll: UCM.PayrollDataType) => {
    deletePayroll(payroll.uuid);
  }, []);

  const handleDownload = useCallback((payroll: UCM.PayrollDataType) => {
    if (!payroll) return;
    setSelectedPayroll(payroll);
  }, []);

  const dateColumns: Array<{
    id: keyof PayrollDataState | string;
    label: string;
  }> = useMemo(
    () => [
      {
        id: 'actions',
        label: '',
        width: 0,
        align: 'center',
        render: (_: unknown, item: PayrollDataState) => {
          const { dateAsString } = item.meta;
          return (
            <Radio
              name="month"
              tabIndex={-1}
              onClick={(event) => pressMonthPayroll(event, item.meta)}
              checked={
                checkedMonthPayroll
                  ? dateAsString === checkedMonthPayroll.dateAsString
                  : false
              }
            />
          );
        },
      },
      {
        id: 'month',
        label: formatMessage(messages.monthColumnLabel),
        render: (_: unknown, item: PayrollDataState) => {
          if (!Object.keys(item.meta).length) return '-';
          const { date, dateAsString } = item.meta;
          return (
            <Typography variant="inherit">
              {date ? toString(date, monthFormat) : dateAsString}
            </Typography>
          );
        },
        align: 'center',
      },
      {
        id: 'notReady',
        label: formatMessage(messages.notReadyColumnLabel),
        render: (_: unknown, item: PayrollDataState) => {
          if (!Object.keys(item.meta).length) return '-';
          const { notReadyCount } = item.meta;
          return <Typography variant="inherit">{notReadyCount}</Typography>;
        },
        align: 'center',
      },
      {
        id: 'ready',
        label: formatMessage(messages.readyColumnLabel),
        render: (_: unknown, item: PayrollDataState) => {
          if (!Object.keys(item.meta).length) return '-';
          const { readyCount } = item.meta;
          return <Typography variant="inherit">{readyCount}</Typography>;
        },
        align: 'center',
      },
      {
        id: 'processed',
        label: formatMessage(messages.processedColumnLabel),
        render: (_: unknown, item: PayrollDataState) => {
          if (!item?.meta) return '-';
          const { paidCount } = item.meta;
          return <Typography variant="inherit">{paidCount}</Typography>;
        },
        align: 'center',
      },
    ],
    [checkedMonthPayroll, pressMonthPayroll],
  );

  const payrollColumns: Array<{
    id: keyof UCM.PayrollDataType | string;
    label: string;
  }> = useMemo(() => {
    const columns = [
      {
        id: 'id',
        label: formatMessage(messages.idColumnLabel),
        align: 'center',
        render: (id: string) => {
          return <Typography variant="inherit">{id}</Typography>;
        },
      },
      {
        id: 'objectKey',
        label: formatMessage(messages.fileNameColumnLabel),
        render: (objectKey: string, payroll: UCM.PayrollDataType) => {
          return (
            <ConfirmationDialog
              message={formatMessage(messages.downloadPayrollConfirmMessage)}
              onConfirm={() => handleDownload(payroll)}
            >
              {(handleOpen) => (
                <Typography
                  variant="inherit"
                  onClick={handleOpen}
                  className={classes.payrollLink}
                >
                  {objectKey}
                </Typography>
              )}
            </ConfirmationDialog>
          );
        },
      },
      {
        id: 'createdAt',
        label: formatMessage(messages.createdOnColumnLabel),
        align: 'center',
        render: (createdAt: string) => {
          return <Typography variant="inherit">{createdAt}</Typography>;
        },
      },
      {
        id: 'actions',
        label: '',
        align: 'center',
        width: 0,
        whiteSpace: 'nowrap',
        render: (_: unknown, payroll: UCM.PayrollDataType) => (
          <ConfirmationDialog
            onConfirm={() => handleDelete(payroll)}
            message={formatMessage(messages.deletePayrollConfirmMessage)}
          >
            {(handleOpen) => (
              <Tooltip arrow title={formatMessage(messages.deleteTooltip)}>
                <IconButton onClick={handleOpen}>
                  <DeleteForeverOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </ConfirmationDialog>
        ),
      },
    ];
    if (!isSuperUser()) columns.splice(columns.length - 1, 1);
    return columns;
  }, []);

  const payrollRows = useMemo(() => {
    const rows = payrollData.filter(({ meta, data }) =>
      selectedMonth
        ? meta.date?.isSame(selectedMonth.date, 'month')
        : meta.date?.year().toString() === selectedYear && data.length > 0,
    );

    return rows.flatMap((payroll) => payroll.data) ?? [];
  }, [payrollData, selectedMonth?.date, selectedYear]);

  const disabledGenerate = useMemo(() => {
    return !checkedMonthPayroll?.readyCount || isCreating || isDeleting;
  }, [checkedMonthPayroll, isCreating, isDeleting]);

  return (
    <PageLayout iconName="payroll" title={formatMessage(messages.title)}>
      <Grid container spacing={4} alignItems="flex-start">
        <Grid container size={{ xs: 12, md: 4 }} spacing={2}>
          <Grid size="grow" />
          <Grid size="auto" height={30}>
            <Dropdown
              name="year"
              options={yearOptions}
              onChange={handleChange}
              value={selectedYear}
              sx={{ pl: 1 }}
            />
          </Grid>
          <Grid size={12}>
            <DataTable
              rows={payrollData}
              columns={dateColumns}
              enabledFooter={false}
              // @ts-ignore to keep active row with different structure of nested data in rows and selected month
              activeRow={selectedMonth ? { meta: selectedMonth } : null}
              className={classes.tableContainer}
              pressRow={(row) => handleSelectMonth(row as PayrollDataState)}
              checkActiveKey="meta.dateAsString"
              loading={isLoading}
            />
          </Grid>
          <Grid container size="auto" spacing={2}>
            <Typography>
              {formatMessage(messages.totalSelectedLabel)}:
            </Typography>
            <Typography variant="h6">
              {checkedMonthPayroll?.readyCount ?? 0}
            </Typography>
          </Grid>
          <Grid size={12}>
            <ConfirmationDialog
              message={formatMessage(messages.createPayrollConfirmMessage, {
                month: toString(checkedMonthPayroll?.date, monthFormat),
              })}
              onConfirm={handleGenerate}
            >
              {(handleOpen) => (
                <Button
                  color="primary"
                  disabled={disabledGenerate}
                  onClick={handleOpen}
                >
                  {formatMessage(messages.generateFilesButton)}
                </Button>
              )}
            </ConfirmationDialog>
          </Grid>
        </Grid>
        <Grid container size={{ xs: 12, md: 8 }} spacing={2}>
          <Grid container spacing={2} height={30} alignItems="flex-end">
            <Typography>{formatMessage(messages.filesForHeader)}:</Typography>
            <Typography variant="h6">
              {selectedMonth
                ? toString(selectedMonth.date, monthYearLongFormat)
                : selectedYear}
            </Typography>
          </Grid>
          <Grid size={12}>
            <DataTable
              rows={payrollRows}
              columns={payrollColumns}
              enabledFooter={false}
              loading={isDeleting || isDownloading}
            />
          </Grid>
        </Grid>
      </Grid>
      <ProgressBar percent={progressData?.percent} />
    </PageLayout>
  );
}

export const useStyles = makeStyles(() => ({
  payrollLink: {
    textDecoration: 'underline',
    cursor: 'pointer',
    '&:hover': {
      fontWeight: 'bold',
    },
  },
  tableContainer: {
    '& .MuiTableCell-head': {
      padding: '8px',
    },
    '& .MuiTableCell-body': {
      padding: '4px 8px',
    },
  },
}));
